import { turnedAdultItemSchema, type TurnedAdultItem } from '@growup/accounts-shared';
import { Button, Card, Field, Input, Notice } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDay, ORIGIN_LABEL } from './labels';

/**
 * Arena youths (accounts without site login) who already turned 18. Giving
 * them an email turns the account into a site account with the same login and
 * sends the activation link; the Arena PIN keeps working and guardians keep
 * access until the person removes it from their account page.
 */
export function AdultsView({ canManage }: { canManage: boolean }) {
  const [list, setList] = useState<TurnedAdultItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/members/youths/adults', z.array(turnedAdultItemSchema))
      .then((rows) => {
        setError(null);
        setList(rows);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  useEffect(load, [load]);

  return (
    <Card headingLevel={2} title="Fizeram 18 anos">
      <p className="text-gu-ink-muted">
        Jovens da Arena que já são adultos e ainda entram só com o PIN. Com um email, a conta passa
        a entrar também no site, com o mesmo login; a pessoa recebe o link para escolher a password.
        Os encarregados mantêm o acesso até a pessoa o retirar na página da conta.
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {list === null ? (
        <p className="text-gu-ink-muted">A carregar…</p>
      ) : list.length === 0 ? (
        <p className="text-gu-ink-muted">Ninguém por agora.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gu-line">
          {list.map((item) => (
            <AdultRow key={item.accountId} item={item} canManage={canManage} onDone={load} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function AdultRow({
  item,
  canManage,
  onDone,
}: {
  item: TurnedAdultItem;
  canManage: boolean;
  onDone: () => void;
}) {
  const [email, setEmail] = useState(item.contactEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="font-semibold">{item.fullName ?? item.login}</span>
        <span className="text-caption text-gu-ink-muted">
          {item.login} · {ORIGIN_LABEL[item.origin]} · nasceu a {formatDay(item.birthDate)}
          {item.guardians > 0
            ? ` · ${String(item.guardians)} encarregado${item.guardians > 1 ? 's' : ''}`
            : ''}
        </span>
      </div>
      {canManage ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setSaving(true);
            send('POST', `/api/members/youths/${item.accountId}/adult-account`, {
              email: email.trim(),
            })
              .then(onDone)
              .catch((err: unknown) => setError(messageOf(err)))
              .finally(() => setSaving(false));
          }}
        >
          <Field label="Email da pessoa" className="min-w-64 flex-1">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" size="sm" className="mb-1.5" disabled={saving}>
            Criar conta do site
          </Button>
        </form>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </li>
  );
}
